// Order Modal - 3-step buy/sell order flow with pre-trade compliance
import React, { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';

export const OrderModal = ({ fund, orderType: initialOrderType = 'buy', onClose }) => {
  const [step, setStep] = useState(1);
  const [orderType, setOrderType] = useState(initialOrderType);
  const [amount, setAmount] = useState('');
  const [shares, setShares] = useState('');
  const [complianceResult, setComplianceResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const calculateShares = () => {
    if (amount && fund.current_price) {
      return (parseFloat(amount) / fund.current_price).toFixed(4);
    }
    return '0';
  };

  const calculateAmount = () => {
    if (shares && fund.current_price) {
      return (parseFloat(shares) * fund.current_price).toFixed(2);
    }
    return '0';
  };

  // Step 1: Enter order details
  const handleStep1Continue = () => {
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    
    setError('');
    setStep(2);
    runComplianceCheck();
  };

  // Step 2: Run compliance check
  const runComplianceCheck = async () => {
    try {
      setLoading(true);
      setError('');

      const orderData = {
        symbol: fund.symbol,
        amount: parseFloat(amount),
        order_type: orderType,
        shares: shares ? parseFloat(shares) : null
      };

      // Call backend to place order (which includes compliance check)
      // For now, we'll simulate the compliance response
      // In production, you might want a dedicated compliance check endpoint
      
      // Simulated compliance check (replace with actual API call if needed)
      const response = await api.post('/investments/order', orderData, {
        validateStatus: (status) => status < 500 // Don't throw on 403
      });

      if (response.status === 403) {
        // Compliance rejected
        const result = response.data?.detail?.compliance_result || {
          approved: false,
          reason: response.data?.detail?.message || 'Order rejected by compliance',
          warnings: [],
          details: {}
        };
        setComplianceResult(result);
      } else if (response.status === 200 || response.status === 201) {
        // Order placed successfully (compliance approved)
        setComplianceResult(response.data.compliance_result);
        
        // If approved, show success and close
        toast.success(
          `Order placed successfully: ${orderType.toUpperCase()} ${calculateShares()} shares of ${fund.symbol}`
        );
        
        setTimeout(() => {
          onClose();
          window.location.reload(); // Refresh to show updated portfolio
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to process order');
      console.error('Order error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Confirm and submit (already handled in step 2 for this flow)
  const handleConfirmOrder = () => {
    // Order already submitted in step 2
    // This is just for display purposes
  };

  const handleClose = () => {
    if (step === 2 && loading) {
      return; // Don't allow closing while processing
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
          {/* Header */}
          <div className="border-b border-[#E1E4E8] px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-[#24292E]">
                  {orderType === 'buy' ? 'Buy' : 'Sell'} {fund.symbol}
                </h2>
                <p className="text-sm text-[#586069] mt-1">{fund.name}</p>
              </div>
              <button
                onClick={handleClose}
                disabled={loading}
                className="text-[#586069] hover:text-[#24292E] disabled:opacity-50"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Step Indicator */}
            <div className="flex items-center gap-2 mt-4">
              {[1, 2].map((s) => (
                <React.Fragment key={s}>
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                    step >= s 
                      ? 'bg-[#0366D6] text-white' 
                      : 'bg-[#F6F8FA] text-[#586069]'
                  }`}>
                    {s}
                  </div>
                  {s < 2 && (
                    <div className={`flex-1 h-1 rounded ${
                      step > s ? 'bg-[#0366D6]' : 'bg-[#E1E4E8]'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-6">
            {/* Step 1: Enter Details */}
            {step === 1 && (
              <div className="space-y-6">
                {/* Order Type Toggle */}
                <div>
                  <label className="block text-sm font-medium text-[#24292E] mb-2">
                    Order Type
                  </label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setOrderType('buy')}
                      className={`flex-1 px-4 py-3 rounded border font-medium transition-colors ${
                        orderType === 'buy'
                          ? 'bg-[#0366D6] text-white border-[#0366D6]'
                          : 'bg-white text-[#586069] border-[#D1D5DB] hover:bg-[#F6F8FA]'
                      }`}
                    >
                      Buy
                    </button>
                    <button
                      onClick={() => setOrderType('sell')}
                      className={`flex-1 px-4 py-3 rounded border font-medium transition-colors ${
                        orderType === 'sell'
                          ? 'bg-[#D73A49] text-white border-[#D73A49]'
                          : 'bg-white text-[#586069] border-[#D1D5DB] hover:bg-[#F6F8FA]'
                      }`}
                    >
                      Sell
                    </button>
                  </div>
                </div>

                {/* Current Price */}
                <div className="bg-[#F6F8FA] rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#586069]">Current Price</span>
                    <span className="text-xl font-bold text-[#24292E]">
                      {formatCurrency(fund.current_price)}
                    </span>
                  </div>
                </div>

                {/* Amount Input */}
                <div>
                  <label className="block text-sm font-medium text-[#24292E] mb-2">
                    Investment Amount
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-3 text-[#586069]">$</span>
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => {
                        setAmount(e.target.value);
                        setShares(''); // Clear shares when amount changes
                      }}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      className="w-full pl-8 pr-4 py-3 border border-[#D1D5DB] rounded text-lg focus:outline-none focus:ring-2 focus:ring-[#0366D6] focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Calculated Shares */}
                {amount && (
                  <div className="bg-[#F6F8FA] rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[#586069]">Shares</span>
                      <span className="text-lg font-semibold text-[#24292E] font-mono">
                        {calculateShares()}
                      </span>
                    </div>
                  </div>
                )}

                {/* Error Display */}
                {error && (
                  <div className="bg-[#FFF5F5] border border-[#FEB2B2] rounded px-4 py-3 text-[#C53030] text-sm">
                    {error}
                  </div>
                )}

                {/* Continue Button */}
                <button
                  onClick={handleStep1Continue}
                  disabled={!amount || parseFloat(amount) <= 0}
                  className="w-full px-6 py-3 bg-[#0366D6] hover:bg-[#0256C5] disabled:bg-[#D1D5DB] disabled:cursor-not-allowed text-white font-medium rounded transition-colors"
                >
                  Continue to Compliance Check
                </button>
              </div>
            )}

            {/* Step 2: Compliance Check & Confirmation */}
            {step === 2 && (
              <div className="space-y-6">
                {loading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#0366D6] mb-4"></div>
                    <p className="text-[#586069]">Running pre-trade compliance check...</p>
                  </div>
                ) : complianceResult ? (
                  <>
                    {/* Compliance Result */}
                    <div className={`rounded-lg p-4 ${
                      complianceResult.approved 
                        ? 'bg-[#E6F7E6] border border-[#28A745]' 
                        : 'bg-[#FFF5F5] border border-[#D73A49]'
                    }`}>
                      <div className="flex items-start gap-3">
                        {complianceResult.approved ? (
                          <svg className="w-6 h-6 text-[#28A745] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        ) : (
                          <svg className="w-6 h-6 text-[#D73A49] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                        <div>
                          <h3 className={`font-semibold ${
                            complianceResult.approved ? 'text-[#28A745]' : 'text-[#D73A49]'
                          }`}>
                            {complianceResult.approved ? 'Compliance Check Passed' : 'Order Blocked by Compliance'}
                          </h3>
                          {complianceResult.reason && (
                            <p className={`text-sm mt-1 ${
                              complianceResult.approved ? 'text-[#22863A]' : 'text-[#C53030]'
                            }`}>
                              {complianceResult.reason}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Compliance Warnings (Amber) */}
                    {complianceResult.warnings && complianceResult.warnings.length > 0 && (
                      <div className="bg-[#FFF9E6] border border-[#FFA500] rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <svg className="w-6 h-6 text-[#FFA500] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          <div className="flex-1">
                            <h3 className="font-semibold text-[#FFA500] mb-2">Compliance Warnings</h3>
                            <ul className="space-y-1">
                              {complianceResult.warnings.map((warning, idx) => (
                                <li key={idx} className="text-sm text-[#8B6914]">
                                  • {warning}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Order Summary */}
                    {complianceResult.approved && (
                      <div className="bg-[#F6F8FA] rounded-lg p-4 space-y-3">
                        <h3 className="font-semibold text-[#24292E]">Order Confirmed</h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-[#586069]">Symbol</span>
                            <span className="font-semibold text-[#24292E] font-mono">{fund.symbol}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[#586069]">Type</span>
                            <span className="font-semibold text-[#24292E] uppercase">{orderType}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[#586069]">Shares</span>
                            <span className="font-semibold text-[#24292E] font-mono">{calculateShares()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[#586069]">Price</span>
                            <span className="font-semibold text-[#24292E]">{formatCurrency(fund.current_price)}</span>
                          </div>
                          <div className="flex justify-between border-t border-[#E1E4E8] pt-2">
                            <span className="text-[#586069]">Total Amount</span>
                            <span className="font-bold text-[#24292E] text-lg">{formatCurrency(amount)}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Error Display */}
                    {error && (
                      <div className="bg-[#FFF5F5] border border-[#FEB2B2] rounded px-4 py-3 text-[#C53030] text-sm">
                        {error}
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-[#E1E4E8] px-6 py-4">
            <div className="flex gap-3 justify-end">
              {step === 1 && (
                <button
                  onClick={handleClose}
                  className="px-6 py-2 border border-[#D1D5DB] text-[#586069] hover:bg-[#F6F8FA] font-medium rounded transition-colors"
                >
                  Cancel
                </button>
              )}
              {step === 2 && !loading && (
                <>
                  {!complianceResult?.approved && (
                    <button
                      onClick={() => setStep(1)}
                      className="px-6 py-2 border border-[#D1D5DB] text-[#586069] hover:bg-[#F6F8FA] font-medium rounded transition-colors"
                    >
                      Back
                    </button>
                  )}
                  <button
                    onClick={handleClose}
                    className="px-6 py-2 bg-[#0366D6] hover:bg-[#0256C5] text-white font-medium rounded transition-colors"
                  >
                    {complianceResult?.approved ? 'Done' : 'Close'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
